import { Users, UserPlus, Globe, UsersRound, Video, Heart } from "lucide-react";
import { Link } from "react-router-dom";

const studyModes = [
  {
    id: "study-with-me",
    title: "Study With Me",
    isH1: true,
    description: "Can't find a study buddy? Study with Me streams let you study alongside a host who broadcasts their session. A growing body of research shows why these sessions work: viewers use \"Study With Me\" videos to personalise their ambience, create peer pressure and gain emotional support. Search interest in these videos tripled in the last five years, reflecting a desire for virtual body-doubling. By watching someone else focus, you're more likely to mirror that behaviour and stay on task. CSW's Study With Me sessions let you pop into a stream, declare your goal and feel supported without needing to coordinate schedules.",
    icon: Video,
    link: "/study-with-me/",
  },
  {
    id: "study-with-friends",
    title: "Study with Friends",
    description: "Invite classmates or colleagues into a private room to work together. Small groups (three to five people) are ideal for accountability and collaboration. Sharing screens or co-editing documents fosters peer teaching, which deepens comprehension. And because CSW includes reminders and group chat, everyone stays on the same page. Whether you're tackling group projects or prepping for exams, studying with friends turns work into a social experience.",
    icon: Users,
    link: "/study-with-friends/",
  },
  {
    id: "study-with-strangers",
    title: "Study With Strangers",
    isH1: true,
    description: "One of the biggest advantages of CSW is its global community. Join a public room and meet students and professionals from around the world. Research on collaborative learning shows that peer support increases engagement and that group size moderates this effect – smaller, focused groups tend to yield higher engagement. Studying with strangers introduces new perspectives, study techniques and motivation. You might find yourself working alongside a medical student in Seoul or an engineer in Toronto; their dedication can inspire yours.",
    icon: Globe,
    link: "/study-with-strangers/",
  },
  {
    id: "group-study",
    title: "Group Study",
    description: "Larger study groups offer diversity and expertise. According to the University of New Hampshire's library guide on online communities, effective virtual groups are built on trust, interdependence and shared goals. CSW supports this by providing tools like shared notes, polls for meeting times and group leaderboards. You can schedule recurring sessions, set collective goals and celebrate achievements together – turning study into a team sport.",
    icon: UsersRound,
    link: "/group-study/",
  },
  {
    id: "zoom-study-room",
    title: "Zoom Study Room",
    description: "Prefer video-first learning? CSW's Zoom Study Rooms integrate high-definition video, screen share and chat. These rooms provide face-to-face accountability and immediate feedback. When students communicate synchronously, they feel more socially and emotionally supported. Video presence also combats camera-off passivity by encouraging engagement and reducing procrastination. For big projects or courses that require discussion, Zoom rooms deliver the structure of a classroom with the convenience of home.",
    icon: Video,
    link: "/zoom-study-room/",
  },
  {
    id: "study-together",
    title: "Study Together Online",
    description: "CSW isn't just a tool – it's a movement. Online study communities foster motivation, accountability and problem-solving by exposing learners to diverse perspectives. Participation in these communities reduces feelings of isolation and creates a sense of belonging. By offering 24/7 rooms, cross-time-zone scheduling and a global user base, CSW ensures you're never alone in your academic journey.",
    icon: Heart,
    link: "/online-study/",
  },
];

export const StudyModes = () => {
  return (
    <section className="py-20 px-4 relative overflow-hidden" aria-labelledby="study-modes-title">
      <div className="absolute inset-0 bg-gradient-to-b from-background via-accent/5 to-background" aria-hidden="true" />
      
      <div className="container mx-auto relative z-10">
        <header className="text-center mb-12 animate-fade-in">
          <h2 id="study-modes-title" className="font-display text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-primary via-secondary to-primary bg-clip-text text-transparent">
            Ways to Study Together
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Find your ideal study environment with CSW's flexible community options
          </p>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {studyModes.map((mode, index) => {
            const Icon = mode.icon;
            return (
              <Link
                key={mode.id}
                to={mode.link}
                className="group p-6 rounded-xl bg-card border-2 border-border hover:border-primary/50 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 animate-fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <Icon className="w-6 h-6 text-primary" />
                  </div>
                  {mode.isH1 ? (
                    <h1 className="text-xl font-bold text-foreground">{mode.title}</h1>
                  ) : (
                    <h3 className="text-xl font-bold text-foreground">{mode.title}</h3>
                  )}
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed line-clamp-6">{mode.description}</p>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};